"use client";
import { postData } from "@/api/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import ErrorMsg from "../common/error-msg";
import styles from "./contact-form.module.css";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const schema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().required().email().label("Email"),
  subject: yup.string().required().min(5).label("Subject"),
  message: yup.string().required().label("Message"),
});

const ContactForm = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);

      const resp = await postData(`/contacts`, data);

      if (!resp?.success) {
        setToastMessage(resp?.message);
        setSuccess(false);
        return;
      }

      setToastMessage(resp?.message || "Message sent successfully!");
      setSuccess(true);
      reset();
    } catch (error) {
      console.log(error as string);
      setToastMessage("Something went wrong. Please try again.");
      setSuccess(false);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setToastMessage("");
      }, 5000);
    }
  });

  return (
    <div className={styles.contactFormWrapper}>
      <form onSubmit={onSubmit} id="contact-form">
        {/* Name & Email Row */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Name <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              {...register("name")}
              placeholder="Enter your name"
              type="text"
              className={styles.formInput}
            />
            <ErrorMsg msg={errors.name?.message!} />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Email <span className={styles.required}>*</span>
            </label>
            <input
              id="email"
              {...register("email")}
              type="email"
              placeholder="Enter your email"
              className={styles.formInput}
            />
            <ErrorMsg msg={errors.email?.message!} />
          </div>
        </div>

        {/* Subject */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            Subject <span className={styles.required}>*</span>
          </label>
          <input
            id="subject"
            {...register("subject")}
            type="text"
            placeholder="What is this about?"
            className={styles.formInput}
          />
          <ErrorMsg msg={errors.subject?.message!} />
        </div>

        {/* Message */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            Message <span className={styles.required}>*</span>
          </label>
          <textarea
            {...register("message")}
            cols={30}
            rows={6}
            placeholder="Write your message here..."
            className={styles.formTextarea}
          ></textarea>
          <ErrorMsg msg={errors.message?.message!} />
        </div>

        {/* Toast Message */}
        {toastMessage && (
          <div
            className={`${styles.toastMessage} ${
              success ? styles.success : styles.error
            }`}
          >
            <div className={styles.toastIcon}>{success ? "✓" : "✕"}</div>
            <span>{toastMessage}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className={styles.submitButton}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className={styles.spinner}></span>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M7 10H13M13 10L10 7M13 10L10 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;